import { useState, useEffect } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { toast } from 'react-toastify';
import { XMarkIcon } from '@heroicons/react/24/outline';

// redux
import { addMediaGuide, editMediaGuide } from '../../stores/mediaguides';

// utilities
import { slugify, isValidSlug } from '../../utilities';

const EMPTY = { resource_name: '', title: '', subtitle: '', url: '' };

/**
 * Detects publicly-embeddable video links and returns a SAFE embed descriptor.
 * We never embed the raw pasted URL — for providers we reconstruct a clean
 * embed URL from the extracted video id. Returns null when not previewable.
 */
const getVideoEmbed = (url = '') => {
  const link = url.trim();
  if (!link) return null;

  // YouTube: watch?v=, youtu.be/, /embed/, /shorts/
  const yt = link.match(/(?:youtube\.com\/(?:watch\?v=|embed\/|shorts\/)|youtu\.be\/)([\w-]{11})/);
  if (yt) return { type: 'iframe', src: `https://www.youtube.com/embed/${yt[1]}` };

  // Vimeo
  const vm = link.match(/vimeo\.com\/(\d+)/);
  if (vm) return { type: 'iframe', src: `https://player.vimeo.com/video/${vm[1]}` };

  // Direct, publicly-servable video file
  if (/^https:\/\/\S+\.(mp4|webm|ogg)(\?\S*)?$/i.test(link)) {
    return { type: 'video', src: link };
  }

  return null;
};

/**
 * Modal for creating or editing a settings Media Guide.
 *   - mediaGuide == null  -> create mode (POST full object)
 *   - mediaGuide provided -> edit mode (PUT only changed fields)
 */
export const ModalAddMediaGuide = ({ isOpen, onClose, mediaGuide = null }) => {

  const dispatch = useDispatch();
  const { creating, updating } = useSelector((state) => state.mediaGuides);
  const isEdit = Boolean(mediaGuide);

  const [form, setForm] = useState(EMPTY);
  const [slugTouched, setSlugTouched] = useState(false);
  const [error, setError] = useState('');

  // pre-fill on open
  useEffect(() => {
    if (isOpen) {
      setForm(mediaGuide ? { ...EMPTY, ...mediaGuide } : EMPTY);
      setSlugTouched(false);
      setError('');
    }
  }, [isOpen, mediaGuide]);

  const setField = (key, value) => setForm((f) => ({ ...f, [key]: value }));

  // auto-generate slug from name until the admin edits it manually (create only)
  const onTitleChange = (value) => {
    setForm((f) => ({
      ...f,
      title: value,
      ...(!isEdit && !slugTouched && { resource_name: slugify(value) }),
    }));
  };

  const handleSubmit = async () => {
    setError('');

    if (isEdit) {
      // diff: only send changed, non-empty fields
      const changes = {};
      ['title', 'subtitle', 'url'].forEach((k) => {
        if (form[k] !== mediaGuide[k] && form[k] !== '') changes[k] = form[k];
      });
      if (Object.keys(changes).length === 0) {
        setError('No changes to save.');
        return;
      }
      try {
        await dispatch(editMediaGuide({ resource_name: mediaGuide.resource_name, changes })).unwrap();
        toast.success('Media guide updated');
        onClose();
      } catch (err) {
        setError(err?.message || 'Failed to update media guide');
      }
      return;
    }

    // create: all fields required + slug valid
    if (!form.title || !form.subtitle || !form.url) {
      setError('All fields are required.');
      return;
    }
    if (!isValidSlug(form.resource_name)) {
      setError('Resource name must be lowercase words separated by single dashes.');
      return;
    }
    try {
      await dispatch(addMediaGuide(form)).unwrap();
      toast.success('Media guide created');
      onClose();
    } catch (err) {
      setError(err?.status === 409 ? 'That resource name is already used.' : (err?.message || 'Failed to create media guide'));
    }
  };

  if (!isOpen) return null;

  const saving = creating || updating;

  // preview only exists once a link is pasted
  const hasPreview = Boolean(form.url?.trim());
  const embed = hasPreview ? getVideoEmbed(form.url) : null;

  // shared form fields (same markup in both layouts)
  const formFields = (
    <div className="p-4 space-y-4">
      <div>
        <label className="block text-sm font-medium text-gray-700">Name</label>
        <input type="text" value={form.title} onChange={(e) => onTitleChange(e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Resource name (slug)</label>
        <input type="text" value={form.resource_name}
          onChange={(e) => { setSlugTouched(true); setField('resource_name', e.target.value); }}
          disabled={isEdit}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm disabled:bg-gray-100" />
        {isEdit && <p className="mt-1 text-xs text-gray-400">Resource name can't be changed after creation.</p>}
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Description</label>
        <textarea rows={3} value={form.subtitle} onChange={(e) => setField('subtitle', e.target.value)}
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm" />
      </div>

      <div>
        <label className="block text-sm font-medium text-gray-700">Link</label>
        <input type="url" value={form.url} onChange={(e) => setField('url', e.target.value)}
          placeholder="https://partner.swifix.my/..."
          className="mt-1 block w-full rounded-md border-gray-300 shadow-sm focus:border-primary-500 focus:ring-primary-500 sm:text-sm" />
        <p className="mt-1 text-xs text-gray-400">This link opens directly in the partner app.</p>
      </div>

      {error && <p className="text-sm text-red-600">{error}</p>}
    </div>
  );

  // shared buttons
  const buttons = (
    <>
      <button onClick={onClose}
        className="px-4 py-2 text-sm font-medium text-gray-700 bg-white border border-gray-300 rounded-md hover:bg-gray-50">Cancel</button>
      <button onClick={handleSubmit} disabled={saving}
        className="px-4 py-2 text-sm font-medium text-white bg-primary-600 rounded-md hover:bg-primary-700 disabled:opacity-50">
        {saving ? 'Saving...' : (isEdit ? 'Save Changes' : 'Create Media Guide')}
      </button>
    </>
  );

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center">
      <div className="fixed inset-0 bg-black/50" onClick={onClose} />

      {/* narrow single-column by default; widen only when a preview is shown */}
      <div className={`relative bg-white rounded-lg shadow-xl w-full mx-4 max-h-[90vh] overflow-y-auto ${hasPreview ? 'max-w-4xl' : 'max-w-lg'}`}>
        <div className="flex items-center justify-between p-4 border-b">
          <h3 className="text-lg font-semibold text-gray-900">{isEdit ? 'Edit Media Guide' : 'Add New Media Guide'}</h3>
          <button onClick={onClose} className="text-gray-400 hover:text-gray-600">
            <XMarkIcon className="h-5 w-5" />
          </button>
        </div>

        {hasPreview ? (
          /* ===== WITH PREVIEW: form left, preview right, buttons under preview ===== */
          <div className="flex flex-col md:flex-row">
            <div className="md:w-1/2 md:border-r border-gray-100">
              {formFields}
            </div>

            <div className="p-4 md:w-1/2 flex flex-col">
              <label className="block text-sm font-medium text-gray-700">Preview</label>
              <div className="mt-1 flex-1">
                {!embed ? (
                  <div className="flex h-full min-h-[180px] items-center justify-center rounded-md border border-dashed border-gray-200 bg-gray-50 p-4">
                    <p className="text-xs text-gray-500 italic text-center">
                      No preview available — this link may be private/protected or an unsupported source. It will still open in the partner app.
                    </p>
                  </div>
                ) : embed.type === 'iframe' ? (
                  <iframe
                    title="Video preview"
                    src={embed.src}
                    className="w-full aspect-video rounded-md border border-gray-200"
                    allow="accelerometer; clipboard-write; encrypted-media; gyroscope; picture-in-picture"
                    allowFullScreen
                  />
                ) : (
                  <video src={embed.src} controls className="w-full aspect-video rounded-md border border-gray-200" />
                )}
              </div>
              <div className="mt-4 flex justify-end gap-2">
                {buttons}
              </div>
            </div>
          </div>
        ) : (
          /* ===== NO PREVIEW: original single-column layout with bottom footer ===== */
          <>
            {formFields}
            <div className="flex justify-end gap-2 p-4 border-t">
              {buttons}
            </div>
          </>
        )}
      </div>
    </div>
  );
};
